// Created: 2026-08-14
// Updated: 2026-08-14
//
// NOTIFICATION query hooks (react-query-notifications, Req 1, 3, 4, 7).
//
// Thin layer, same shape as slices 1–5: the transport stays in `src/api/**` and is NOT duplicated here,
// and the migrated surfaces consume these hooks instead of calling `secureFetch` themselves. BOTH hooks
// forward React Query's `signal` into their fetcher — load-bearing, not an unused parameter: it is what
// turns navigating away mid-load into a CANCELLATION rather than a query error (design § Error
// Handling, Intentional Delta 1).
//
// The writes live in `mutations.ts`. Together they are what replaces `NotificationCenter`'s hand-rolled
// `CacheMap`/`CategoryCache` component-state cache and its optimistic counter arithmetic, and what
// replaces `NotificationBell`'s private `allNotifications` array: an invalidation refetches in the
// BACKGROUND, so neither subtree is unmounted and the open popover / selected detail row survives.

import type { NotificationCategory } from '@mrb91002/shared-types'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { fetchNotificationsPaginated, getUnreadNotificationCount } from '../../api/secure/notifications'
import { useAuth } from '../../context/AuthContext'

import { NOTIFICATION_STALE_TIME_MS, normalizeCategory, notificationKeys } from './keys'

/**
 * PERSONAL — the caller's unread counts. Key `['me','notifications','unreadCount']`.
 *
 * **THE CONSOLIDATED READ.** One entry, one request, read by the nav bell's badge AND by the
 * Notification Center's five category badges (both through `hooks/useNotifications.ts`). The full
 * three-implementations-of-one-number history, and the proof that the bell's old context field and this
 * endpoint's `count` come from the same DynamoDB record and attribute, is on
 * `notificationKeys.unreadCount`.
 *
 * `enabled: !!idToken` replaces the pre-migration `if (!idToken) { setUnreadCount(0); return }` inside
 * the hook's effect, so a signed-out caller fires no request and simply holds no data (Req 1.3).
 */
export function useUnreadNotificationCountQuery() {
  const { idToken } = useAuth()

  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    // `signal` forward — the abort-as-cancellation seam. Keep it.
    queryFn: ({ signal }) => getUnreadNotificationCount(idToken as string, { signal }),
    enabled: !!idToken,
    staleTime: NOTIFICATION_STALE_TIME_MS,
  })
}

/**
 * PERSONAL — one category's notifications, accumulated. Key
 * `['me','notifications','list',{category}]`, cursor in `pageParam`.
 *
 * ONE cache entry per category holds `{pages, pageParams}`, so `data.pages.flatMap(p => p.notifications)`
 * reproduces exactly the sequence the pre-migration `[...prevNotifs, ...result.notifications]` produced
 * (Req 4.4). `hasNextPage` reproduces `hasMore: !!result.lastKey`.
 *
 * THE CATEGORY IS NORMALIZED ON BOTH PATHS — into the key AND into the fetcher — so the key and the URL
 * describe the same request (slice 4 rule 2). That is what makes the nav bell (`useNotificationsQuery()`,
 * no argument) and the Notification Center's "All" tab (`useNotificationsQuery('all')`) land on ONE entry
 * and issue ONE request for a URL that was already byte-identical. See `normalizeCategory`.
 *
 * `options.enabled` is how `NotificationCenter` keeps its on-demand per-category loading: it mounts one
 * hook per category and enables only the ACTIVE one, so switching tabs fetches that tab and nothing
 * else. That is the pre-migration `if (existing?.loaded) return` latch expressed as an `enabled`
 * condition — the same move slice 4 made for the minimum-length search guard (Req 4.6).
 *
 * ─── THE CURSOR IS BROKEN ON THE WIRE, AND THIS SLICE PRESERVES THAT ─────────────────────────────
 *
 * `getNextPageParam` below returns `lastPage.lastKey`, which is what today's
 * `setLastKey(result.lastKey)` stored. That value is NOT a string:
 * `lambda-secure/src/routes/get-notifications-paginated.ts:44-46` returns DynamoDB's
 * `LastEvaluatedKey` OBJECT through a field the shared type declares as `lastKey: string | null`
 * (`packages/shared-types/src/api/notification.ts:50-53`). The transport then calls
 * `params.set('lastKey', lastKey)`, and `URLSearchParams.set` stringifies its argument, so the wire
 * carries `lastKey=%5Bobject+Object%5D`; the handler's `JSON.parse(decodeURIComponent(...))` throws and
 * the `catch` answers **500**. "Load more" therefore cannot work today in any category with more than 10
 * notifications.
 *
 * This is PRE-EXISTING and deliberately NOT fixed here (Req 3.5, 3.6): fixing it means changing the
 * handler's response type or its parse, i.e. a backend + shared-type change with its own denominator
 * conversation. The behavior is preserved exactly — the same value goes to the same fetcher line, so the
 * request bytes are unchanged. Two consequences, recorded so neither is mistaken for a slice-6
 * regression:
 *   1. Failure stays SILENT. `fetchNextPage` rejects, React Query records the error on the entry, `data`
 *      is RETAINED, and the component renders from `data` — so the already-loaded pages stay on screen
 *      and no error text appears, exactly as the pre-migration `console.error`-and-continue did.
 *   2. THREE requests where there was one. The shared `shouldRetryQuery` retries a 5xx up to
 *      `MAX_QUERY_RETRIES`. That is the shared client's policy, load-bearing since slice 1, and is not
 *      overridden for one surface. Invisible in the render.
 */
export function useNotificationsQuery(category?: NotificationCategory, options?: { enabled?: boolean }) {
  const { idToken } = useAuth()
  const normalized = normalizeCategory(category)

  return useInfiniteQuery({
    queryKey: notificationKeys.list(normalized),
    queryFn: ({ pageParam, signal }) =>
      fetchNotificationsPaginated(pageParam, idToken as string, normalized, { signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.lastKey ?? undefined,
    enabled: !!idToken && (options?.enabled ?? true),
    staleTime: NOTIFICATION_STALE_TIME_MS,
  })
}
